<script>
import { mapMutations, mapState, mapGetters } from 'vuex';
import FormLayout from '@/layouts/FormLayout.vue';
import FormPaymentButtons from '@/components/FormPaymentButtons.vue';
import AppAccordion from '@/common/AppAccordion.vue';
import CommentForm from '@/components/CommentForm.vue';
import CustomQuestions from '@/components/CustomQuestions.vue';
import DedicationForm from '@/components/DedicationForm.vue';
import Plaque from '@/components/Plaque.vue';

const LINKS_FAMILY_ORG_ID = 'fd4afae0-1eb8-11ef-9973-e38c945a9135';
const PLAQUE_QUESTION_TEXT = "niftar's name";

export default {
  name: 'AdditionalDetails',
  components: {
    FormLayout,
    FormPaymentButtons,
    AppAccordion,
    CommentForm,
    CustomQuestions,
    DedicationForm,
    Plaque
  },
  props: {
    active: Boolean
  },

  data() {
    return {
      donateWithCompany: !!this.$store.state.donation.donorDetails.companyName,
      commentEnabled: false,
      attemptedSubmit: false,
      paddingBottom: 200
    };
  },

  computed: {
    ...mapState(['orgId']),
    ...mapState('donation', { donation: (state) => state }),
    ...mapState('campaign', {
      campaignSettings: (campaign) => campaign.settings
    }),
    ...mapState('publicOrgSettings', ['settings']),
    ...mapGetters('validation', ['isCustomQuestionsValid']),
    companyName: {
      get() {
        return this.donation.donorDetails.companyName || '';
      },
      set(value) {
        this.$set(this.donation.donorDetails, 'companyName', value);
      }
    },
    settingsComputed() {
      return this.campaignSettings || this.settings;
    },

    isCommentShown() {
      return !this.settingsComputed?.hideComment;
    },

    isDedicationShown() {
      return !this.settingsComputed?.hideDedication;
    }
  },

  methods: {
    ...mapMutations(['SET_SHOW_PAYMENT_BUTTONS']),
    ...mapMutations('donation', ['SET_COMMENT', 'SET_IS_DEDICATING', 'SET_DEDICATION', 'SET_IS_DONATING_ANONYMOUSLY']),

    isPlaqueQuestion(customQuestion) {
      if (this.orgId !== LINKS_FAMILY_ORG_ID) {
        return false;
      }

      const questionText = (customQuestion?.question || '').trim().toLowerCase().replace(/[‘’]/g, "'");
      return questionText.includes(PLAQUE_QUESTION_TEXT);
    },

    formatCustomQuestionAnswer(answer) {
      if (Array.isArray(answer)) {
        return answer.filter(Boolean).join(', ');
      }

      if (typeof answer === 'string') {
        return answer.trim();
      }

      return answer || '';
    },

    async donateClicked() {
      // Reset and re-set to ensure watchers fire even on repeated clicks
      this.attemptedSubmit = false;
      await this.$nextTick();
      this.attemptedSubmit = true;

      let valid = true;

      if (!this.isCustomQuestionsValid) valid = false;

      if (this.isDedicationShown && this.donation.isDedicating) {
        if (!(await this.$refs.dedicationForm?.validate())) valid = false;
      }

      if (this.isCommentShown && this.commentEnabled) {
        if (!(await this.$refs.commentForm?.validate())) valid = false;
      }

      if (!this.settingsComputed?.hideCompanyDonation && this.donateWithCompany) {
        this.$refs.companySubmit.click();
        await this.$nextTick();
        if (await this.$refs.companyForm.hasValidationErrors()) valid = false;
      }

      if (!valid) return;

      this.SET_SHOW_PAYMENT_BUTTONS(true);
    },

    onCompanyEnabledChange(enabled) {
      this.donateWithCompany = enabled;
      if (!enabled) this.companyName = '';
    },

    onCommentEnabledChange(enabled) {
      this.commentEnabled = enabled;

      // Clear comment when disabled
      if (!enabled) {
        this.SET_COMMENT(null);
      }
    },

    onDedicationEnabledChange(enabled) {
      this.SET_IS_DEDICATING(enabled);

      if (!enabled) {
        this.SET_DEDICATION({
          message: '',
          public: false,
          notifyByEmail: false,
          email: ''
        });
      }
    }
  },

  created() {
    if (this.donation.comment) {
      this.commentEnabled = true;
    }
  },
  mounted() {
    this.paddingBottom = this.$refs.footerSticky.$el.offsetHeight;
  }
};
</script>

<template>
  <form-layout title="Additional details" :style="`padding-bottom: ${paddingBottom}px`" :show-logo="false">
    <div class="additional-details">
      <custom-questions v-if="settingsComputed?.isCustomQuestionsActive" :fields-dirty="attemptedSubmit" class="mt-8">
        <template #after-question="{ question }">
          <plaque v-if="isPlaqueQuestion(question)" :text="formatCustomQuestionAnswer(question.answer)" class="mt-8" />
        </template>
      </custom-questions>

      <div class="my-8">
        <div class="flex flex-col gap-3">
          <app-accordion
            v-if="isDedicationShown"
            title="Add a dedication message"
            icon-src="https://media.double.giving/images/additional-dedication.svg"
            :expanded="donation.isDedicating"
            :show-checkbox="true"
            :checkbox-value="donation.isDedicating"
            @checkbox-change="onDedicationEnabledChange"
            @toggle="onDedicationEnabledChange"
          >
            <dedication-form ref="dedicationForm" />
          </app-accordion>

          <app-accordion
            v-if="isCommentShown"
            title="Leave a comment"
            icon-src="https://media.double.giving/images/additional-comment.svg"
            :expanded="commentEnabled"
            :show-checkbox="true"
            :checkbox-value="commentEnabled"
            @checkbox-change="onCommentEnabledChange"
            @toggle="onCommentEnabledChange"
          >
            <comment-form ref="commentForm" />
          </app-accordion>

          <app-accordion
            v-if="!settingsComputed?.hideAnonymously"
            icon-src="https://media.double.giving/images/additional-anonymous.svg"
            :show-checkbox="true"
            :checkbox-value="donation.anonymous"
            @toggle="SET_IS_DONATING_ANONYMOUSLY(!donation.anonymous)"
          >
            <template #title>
              <div class="flex items-center gap-1.5">
                <span>Donate anonymously</span>
                <app-tooltip>
                  <app-icon name="info" type="svg" width="14" height="14" class="accordion-info-icon text-blue-04 fill-current" />
                  <template #content>
                    <p>Your name won’t be displayed publicly</p>
                  </template>
                </app-tooltip>
              </div>
            </template>
          </app-accordion>

          <app-accordion
            v-if="!settingsComputed?.hideCompanyDonation"
            :title="$t('payment.behalfCompany')"
            icon-src="https://media.double.giving/images/additional-company.svg"
            :expanded="donateWithCompany"
            :show-checkbox="true"
            :checkbox-value="donateWithCompany"
            @checkbox-change="onCompanyEnabledChange"
          >
            <formulate-form ref="companyForm">
              <formulate-input
                v-model="companyName"
                type="text"
                validation="required"
                validation-name="Company name"
                :label="$t('payment.companyName')"
                :placeholder="$t('payment.companyName')"
                name="companyName"
                autocomplete="organization"
                class="w-full"
              />
              <button ref="companySubmit" type="submit" class="hidden"></button>
            </formulate-form>
          </app-accordion>
        </div>
      </div>
    </div>

    <template #footer>
      <footer-sticky @donate-clicked="donateClicked" ref="footerSticky"> </footer-sticky>
      <form-payment-buttons ref="paymentSection" />
    </template>
  </form-layout>
</template>
